import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Terminal, 
  Compass, 
  FolderKanban, 
  Copy, 
  Check, 
  ExternalLink, 
  FileDown, 
  Mail, 
  Phone, 
  X,
  Code2,
  Sparkles
} from 'lucide-react';
import GithubIcon from './icons/GithubIcon';

const CommandPalette = ({ isOpen, onClose, onSelectProject }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [copiedText, setCopiedText] = useState(null);
  const [cliOutput, setCliOutput] = useState(null);
  const inputRef = useRef(null);

  // Focus input on open
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setCliOutput(null);
      setTimeout(() => inputRef.current?.focus(), 50);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  // Handle global shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (isOpen) onClose();
        else window.dispatchEvent(new CustomEvent('open-command-palette'));
      }
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => {
      setCopiedText(null);
      onClose();
    }, 1200);
  };

  const executeCommand = (cmd) => {
    const cleanCmd = cmd.trim().toLowerCase();
    switch (cleanCmd) {
      case 'whoami':
        setCliOutput({
          cmd: 'whoami',
          res: "Ravindran Sanjeyan • Full Stack Software Engineer • BEng (Hons) Software Engineering • Jaffna, Sri Lanka."
        });
        return true;
      case 'skills':
        setCliOutput({
          cmd: 'skills',
          res: "Core: React, Next.js, Node.js, C#, ASP.NET Core, TypeScript, SQL Server, MongoDB, Tailwind CSS, Electron."
        });
        return true;
      case 'contact':
        setCliOutput({
          cmd: 'contact',
          res: "Email: sanjeyan98@gmail.com | Phone: +94 77 611 9668 | GitHub: github.com/RSanjeyan"
        });
        return true;
      case 'clear':
        setCliOutput(null);
        setQuery('');
        return true;
      case 'cv':
      case 'resume':
        window.open('/assets/cv.pdf', '_blank');
        setCliOutput({
          cmd: cleanCmd,
          res: "Opening Curriculum Vitae (PDF) in new tab... Download ready at /assets/cv.pdf"
        });
        return true;
      case 'help':
        setCliOutput({
          cmd: 'help',
          res: "Available CLI commands: whoami, skills, contact, cv, clear, matrix, exit"
        });
        return true;
      case 'matrix':
        setCliOutput({
          cmd: 'matrix',
          res: "Wake up, Neo... Follow the white rabbit 🐇"
        });
        return true;
      default:
        return false;
    }
  };

  // Base list of commands
  const allItems = [
    // Navigation
    {
      id: 'nav-home',
      group: 'Navigation',
      title: 'Go to Home',
      description: 'Back to top overview',
      icon: Compass,
      action: () => {
        window.location.hash = 'home';
        onClose();
      }
    },
    {
      id: 'nav-about',
      group: 'Navigation',
      title: 'Go to About Me',
      description: 'Background, education, and stats',
      icon: Compass,
      action: () => {
        window.location.hash = 'about';
        onClose();
      }
    },
    {
      id: 'nav-skills',
      group: 'Navigation',
      title: 'Go to Technical Arsenal',
      description: 'Languages, frameworks, and tools',
      icon: Code2,
      action: () => {
        window.location.hash = 'skills';
        onClose();
      }
    },
    {
      id: 'nav-services',
      group: 'Navigation',
      title: 'Go to Services',
      description: 'Web development, desktop apps & API design',
      icon: Compass,
      action: () => {
        window.location.hash = 'services';
        onClose();
      }
    },
    {
      id: 'nav-projects',
      group: 'Navigation',
      title: 'Go to Selected Work',
      description: 'Featured client & engineering projects',
      icon: FolderKanban,
      action: () => {
        window.location.hash = 'projects';
        onClose();
      }
    },
    {
      id: 'nav-experience',
      group: 'Navigation',
      title: 'Go to Experience & Timeline',
      description: 'Work history and academic qualifications',
      icon: Compass,
      action: () => {
        window.location.hash = 'experience';
        onClose();
      }
    },
    {
      id: 'nav-contact',
      group: 'Navigation',
      title: 'Go to Contact',
      description: 'Get in touch for projects and collaborations',
      icon: Mail,
      action: () => {
        window.location.hash = 'contact';
        onClose();
      }
    },

    // Projects (Case Studies)
    {
      id: 'proj-fgc',
      group: 'Case Studies',
      title: 'Case Study: FGC Presenter',
      description: 'Dual-screen presentation software (React + Electron)',
      icon: FolderKanban,
      action: () => {
        onSelectProject('fgc-presenter');
        onClose();
      }
    },
    {
      id: 'proj-smartmed',
      group: 'Case Studies',
      title: 'Case Study: SmartMed Pharmacy',
      description: 'Inventory & POS management (C# .NET + SQL Server)',
      icon: FolderKanban,
      action: () => {
        onSelectProject('smartmed-pharmacy');
        onClose();
      }
    },
    {
      id: 'proj-church',
      group: 'Case Studies',
      title: 'Case Study: Church Management System',
      description: 'Cloud parishioner directory & donation tracking (React + Firebase)',
      icon: FolderKanban,
      action: () => {
        onSelectProject('church-management');
        onClose();
      }
    },

    // Quick Actions
    {
      id: 'action-copy-email',
      group: 'Quick Actions',
      title: 'Copy Email Address',
      description: 'sanjeyan98@gmail.com',
      icon: Copy,
      action: () => copyToClipboard('sanjeyan98@gmail.com', 'Email copied!')
    },
    {
      id: 'action-copy-phone',
      group: 'Quick Actions',
      title: 'Copy Phone Number',
      description: '+94 77 611 9668',
      icon: Phone,
      action: () => copyToClipboard('+94776119668', 'Phone copied!')
    },
    {
      id: 'action-download-cv',
      group: 'Quick Actions',
      title: 'Download Curriculum Vitae',
      description: 'Get latest PDF resume (Ravindran Sanjeyan)',
      icon: FileDown,
      action: () => {
        const link = document.createElement('a');
        link.href = '/assets/cv.pdf';
        link.target = '_blank';
        link.setAttribute('download', 'Ravindran_Sanjeyan_CV.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        onClose();
      }
    },
    {
      id: 'action-github',
      group: 'Quick Actions',
      title: 'Open GitHub Profile',
      description: 'github.com/RSanjeyan',
      icon: GithubIcon,
      action: () => {
        window.open('https://github.com/RSanjeyan', '_blank');
        onClose();
      }
    }
  ];

  // Filter items based on query
  const filteredItems = allItems.filter(item => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      item.title.toLowerCase().includes(q) ||
      item.description.toLowerCase().includes(q) ||
      item.group.toLowerCase().includes(q)
    );
  });

  // Handle arrow key navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % (filteredItems.length || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + filteredItems.length) % (filteredItems.length || 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Check CLI command first
      if (executeCommand(query)) return;

      if (filteredItems[selectedIndex]) {
        filteredItems[selectedIndex].action();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[120] flex items-start justify-center pt-16 sm:pt-24 p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/80 backdrop-blur-md transition-opacity"
        />

        {/* Command Box */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{ type: "spring", duration: 0.4, bounce: 0.1 }}
          className="relative w-full max-w-2xl bg-[#0F0F0F] border border-[#2A2A2A] rounded-3xl shadow-2xl overflow-hidden z-10 text-white flex flex-col"
        >
          {/* Search Header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-[#222]">
            <Search size={20} className="text-slate-400 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelectedIndex(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search sections, case studies, or try 'whoami' / 'skills'..."
              className="w-full bg-transparent text-white placeholder-slate-500 text-base sm:text-lg outline-none font-medium"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="text-slate-500 hover:text-white text-xs px-2 py-1 rounded bg-[#1A1A1A]"
              >
                Clear
              </button>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white bg-[#1A1A1A] hover:bg-[#262626] border border-[#333] transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          {/* Copied Feedback Banner */}
          {copiedText && (
            <div className="bg-emerald-500/10 border-b border-emerald-500/20 text-emerald-400 px-5 py-2.5 text-sm flex items-center gap-2">
              <Check size={16} />
              <span>{copiedText}</span>
            </div>
          )}

          {/* CLI Terminal Output Box (if triggered) */}
          {cliOutput && (
            <div className="bg-[#080808] border-b border-[#222] p-4 font-mono text-xs sm:text-sm text-emerald-400">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <Terminal size={14} />
                <span>terminal ~ {cliOutput.cmd}</span>
              </div>
              <p className="leading-relaxed text-slate-200">{cliOutput.res}</p>
            </div>
          )}

          {/* Results List */}
          <div className="max-h-[380px] overflow-y-auto p-2 space-y-1 custom-scrollbar">
            {filteredItems.length === 0 ? (
              <div className="py-12 text-center text-slate-500 text-sm">
                <Sparkles size={24} className="mx-auto mb-2 opacity-50" />
                <p>No results found for "{query}"</p>
                <p className="text-xs text-slate-600 mt-1">
                  Try typing <span className="text-slate-400 font-mono">whoami</span>, <span className="text-slate-400 font-mono">skills</span>, or <span className="text-slate-400 font-mono">contact</span>
                </p>
              </div>
            ) : (
              filteredItems.map((item, index) => {
                const Icon = item.icon;
                const isSelected = index === selectedIndex;
                return (
                  <button
                    key={item.id}
                    onClick={() => item.action()}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl text-left transition-all ${
                      isSelected
                        ? 'bg-white text-black font-semibold shadow-sm'
                        : 'text-slate-300 hover:bg-[#1A1A1A]'
                    }`}
                  >
                    <div className="flex items-center gap-3.5 min-w-0">
                      <div
                        className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                          isSelected
                            ? 'bg-black text-white'
                            : 'bg-[#181818] text-slate-400 border border-[#2B2B2B]'
                        }`}
                      >
                        <Icon size={18} />
                      </div>
                      <div className="truncate">
                        <div className={`text-sm ${isSelected ? 'text-black font-bold' : 'text-slate-200 font-medium'}`}>
                          {item.title}
                        </div>
                        <div className={`text-xs truncate ${isSelected ? 'text-slate-700' : 'text-slate-500 font-light'}`}>
                          {item.description}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pl-2 shrink-0">
                      <span className={`text-[10px] uppercase font-mono px-2 py-0.5 rounded-full ${
                        isSelected 
                          ? 'bg-black/10 text-slate-800' 
                          : 'bg-[#181818] text-slate-500 border border-[#292929]'
                      }`}>
                        {item.group}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer Shortcuts */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-[#222] bg-[#0A0A0A] text-[11px] text-slate-500 font-mono">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-[#1A1A1A] border border-[#333] text-slate-300">↑↓</kbd> navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-[#1A1A1A] border border-[#333] text-slate-300">↵</kbd> select
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-[#1A1A1A] border border-[#333] text-slate-300">esc</kbd> close
              </span>
            </div>
            <div className="hidden sm:block text-slate-600">
              Ctrl+K / ⌘K
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CommandPalette;
